import React, { createContext, useContext, useState, useEffect } from 'react';

const SubscriptionContext = createContext();

const API_BASE_URL = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';

export function SubscriptionProvider({ children }) {
    const [subscription, setSubscription] = useState(null);
    const [trial, setTrial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch subscription status
    const fetchSubscriptionStatus = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/subscription-status/`, {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            const data = await response.json();
            
            if (data.success) {
                setSubscription(data.subscription);
                setTrial(data.trial || null);
                setError(null);
            } else {
                setError(data.error);
                setSubscription(null);
                setTrial(null);
            }
        } catch (err) {
            setError('Failed to load subscription status');
            setSubscription(null);
            setTrial(null);
        } finally {
            setLoading(false);
        }
    };

    // Check feature permission
    const checkPermission = async (feature) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/check-permission/?feature=${encodeURIComponent(feature)}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Update trial info if returned
                if (data.trial) {
                    setTrial(data.trial);
                }
                return {
                    hasAccess: data.has_access,
                    planName: data.plan_name,
                    status: data.status,
                    trial: data.trial
                };
            }
            
            return { hasAccess: false, error: data.error };
        } catch (err) {
            return { hasAccess: false, error: 'Failed to check permission' };
        }
    };

    // Track feature usage
    const trackUsage = async (usageType, featureName = '', metadata = {}) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/track-usage/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    usage_type: usageType,
                    feature_name: featureName,
                    metadata
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Update trial info if returned
                if (data.trial) {
                    setTrial(data.trial);
                }
                return { success: true, trial: data.trial };
            }
            
            return { success: false, error: data.error, trial: data.trial_info };
        } catch (err) {
            return { success: false, error: 'Failed to track usage' };
        }
    };

    // Get subscription plans
    const getSubscriptionPlans = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/subscription-plans/`, {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            const data = await response.json();
            return data.success ? data.plans : [];
        } catch (err) {
            return [];
        }
    };

    // Upgrade subscription (placeholder)
    const upgradeSubscription = async (planId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/upgrade-subscription/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ plan_id: planId })
            });
            
            const data = await response.json();
            return data;
        } catch (err) {
            return { success: false, error: 'Failed to upgrade subscription' };
        }
    };

    // Helper functions
    const isTrialUser = () => subscription?.status === 'trial';
    const isPaidUser = () => subscription?.status === 'active';
    const hasFeatureAccess = (feature) => subscription?.features?.[feature] || false;
    const getTrialRemaining = () => trial?.remaining || 0;
    const getTrialUsage = () => trial ? `${trial.usage_count}/${trial.usage_limit}` : '';

    // Load subscription status on mount and when user changes
    useEffect(() => {
        // Only fetch if user appears to be logged in
        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        const userData = localStorage.getItem('user');
        
        if (isLoggedIn && userData) {
            fetchSubscriptionStatus();
        } else {
            setSubscription(null);
            setTrial(null);
            setError(null);
            setLoading(false);
        }
    }, []);
    
    // Refresh subscription when user changes (listen to localStorage changes)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'user' || e.key === 'isLoggedIn') {
                const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
                const userData = localStorage.getItem('user');
                
                if (isLoggedIn && userData) {
                    fetchSubscriptionStatus();
                } else {
                    setSubscription(null);
                    setTrial(null);
                    setError(null);
                    setLoading(false);
                }
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const value = {
        subscription,
        trial,
        loading,
        error,
        
        // Functions
        fetchSubscriptionStatus,
        refreshSubscription: fetchSubscriptionStatus, // Alias for manual refresh
        checkPermission,
        trackUsage,
        getSubscriptionPlans,
        upgradeSubscription,
        
        // Helper functions
        isTrialUser,
        isPaidUser,
        hasFeatureAccess,
        getTrialRemaining,
        getTrialUsage,
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
}

export function useSubscription() {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
}

export default SubscriptionContext; 